import { Link } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { Btn, H3, P, H6 } from "../../../AbstractElements";
import { ViewCompanyProfile } from "../../../Types/CompanyProfile/CompanyProfile";
import { Edit } from "react-feather";
import { Modal, ModalHeader } from "reactstrap";
import { useState } from "react";
import { useMutation, useQueryClient, useQuery } from "@tanstack/react-query";

import {
  companyprofileService,
  COMPANY_QUERY_KEY,
  VIEW_COMPANY_QUERY_KEY,
} from "../../../Services/companyProfileService";

//Modals
import UpdateCompanyNameModal from "../../UiKits/Modal/ClientProfile/Update/UpdateCompanyDetails";
import UpdateBillingModal from "../../UiKits/Modal/ClientProfile/Update/UpdateBillingDetails";
import UpdateAddressModal from "../../UiKits/Modal/ClientProfile/Update/UpdateAddressDetails";

type CompanyDataProps = {
  companyInfo?: ViewCompanyProfile;
};
export const CompanyInfoSection = ({ companyInfo }: CompanyDataProps) => {
  const [CompanyNamemodal, setModal] = useState(false);
  const toggleCompName = () => setModal(!CompanyNamemodal);
  const [CompanyBillingmodal, setBillingModal] = useState(false);
  const toggleBilling = () => setBillingModal(!CompanyBillingmodal);
  const [CompanyAddressmodal, setAddressModal] = useState(false);
  const toggleAddress = () => setAddressModal(!CompanyAddressmodal);
  const company_code = companyInfo?.code || "";

  // FETCH COMPANY DETAILS
  const { data: CompanyData, isLoading: isFetching } = useQuery({
    queryKey: [VIEW_COMPANY_QUERY_KEY, company_code],
    queryFn: () => companyprofileService.getViewCompanyProfile(company_code),
    enabled: !!company_code,
  });

  // Extract company data safely
  const company = CompanyData?.data;

  // Partnership end date display logic
  let endDateDisplay = "";
  if (company) {
    if (company.status === 0) {
      endDateDisplay = company.end_date ? `- ${company.end_date}` : "";
    } else {
      endDateDisplay = company.end_date ? `- ${company.end_date}` : "- Present";
    }
  }

  if (isFetching || !company) {
    return <div>Loading...</div>;
  }

  return (
    <Row className="g-3">
      {/* Left Column */}
      <Col sm="6" lg="4" className="order-sm-1 order-xl-0">
        <Row>
          <Col md="7">
            <div className="ttl-info text-start">
              <H6 className="mb-1">
                <i className="fa fa-calendar me-2"></i> PARTNERSHIP START DATE
              </H6>
              <span className="mb-2">
                {company.start_date} {endDateDisplay}
              </span>

              <H6 className="mb-1">
                <i className="fa fa-tag me-2"></i> PARTNERSHIP STATUS
              </H6>
              <span
                style={{
                  color: company.status_label === "ACTIVE" ? "green" : "red",
                }}
              >
                <b>{company.status_label}</b>
              </span>

              {company.remarks && (
                <>
                  <br />
                  {company.remarks}
                </>
              )}
            </div>
          </Col>

          <Col md="5">
            <div className="ttl-info text-start">
              <H6 className="mb-1">
                <i className="fa fa-user me-2"></i> CHIEF EXECUTIVE OFFICER
              </H6>
              <span className="mb-2">
                {company.owner_name ?? <i>** No Record Found **</i>}
              </span>

              <H6 className="mb-1">
                <i className="fa fa-book me-2"></i> COMPANY TIN
              </H6>
              <span className="mb-2">{company.tin_id}</span>
            </div>
          </Col>
        </Row>
      </Col>

      {/* Center Column */}
      <Col sm="12" lg="4" className="order-sm-0 order-xl-1">
        <div className="user-designation">
          <div className="title">
            <br></br>
            <Link to="#" onClick={toggleCompName}>
              <Edit size={16} /> {`${company.company} (${company.code})`}
            </Link>

            {company && (
              <UpdateCompanyNameModal
                toggle={toggleCompName}
                modal={CompanyNamemodal}
                company_code={company.code}
                company_name={company.company}
              />
            )}
          </div>
          <div className="desc">{company.industry}</div>
        </div>
      </Col>

      {/* Right Column */}
      <Col sm="6" lg="4" className="order-sm-2 order-xl-2">
        <Row>
          <Col md="6">
            <div className="ttl-info text-start">
              <H6 className="mb-1">
                <i className="fa fa-location-arrow me-2"></i> BILLING ADDRESS
              </H6>
              <span>
                <Link to="#" onClick={toggleBilling}>
                  <Edit size={12} />{" "}
                </Link>
                {company && (
                  <UpdateBillingModal
                    toggle={toggleBilling}
                    modal={CompanyBillingmodal}
                    company_code={company.code}
                    company_name={company.company}
                  />
                )}
                {[company.billing_address_line1, company.billing_address_line2]
                  .filter(Boolean)
                  .join(", ")}
                {company.billing_barangay
                  ? `, Barangay ${company.billing_barangay}`
                  : ""}
                {company.billing_city ? `, ${company.billing_city}` : ""}
                {company.billing_province
                  ? `, ${company.billing_province}`
                  : ""}
                {company.billing_zip_code ? ` ${company.billing_zip_code}` : ""}
                {company.billing_country ? `, ${company.billing_country}` : ""}
              </span>
            </div>
          </Col>

          <Col md="6">
            <div className="ttl-info text-start">
              <H6 className="mb-1">
                <i className="fa fa-map-marker me-2"></i> BUSINESS ADDRESS
              </H6>
              <span>
                <Link to="#" onClick={toggleAddress}>
                  <Edit size={12} />{" "}
                </Link>
                {company && (
                  <UpdateAddressModal
                    toggle={toggleAddress}
                    modal={CompanyAddressmodal}
                    company_code={company.code}
                    company_name={company.company}
                  />
                )}
                {[company.address_line1, company.address_line2]
                  .filter(Boolean)
                  .join(", ")}
                {company.barangay ? `, Barangay ${company.barangay}` : ""}
                {company.city ? `, ${company.city}` : ""}
                {company.province ? `, ${company.province}` : ""}
                {company.zip_code ? ` ${company.zip_code}` : ""}
                {company.country ? `, ${company.country}` : ""}
              </span>
            </div>
          </Col>
        </Row>
      </Col>
    </Row>
  );
};
